import {
	Badge,
	Stack,
	Icons,
	Text,
	RelativeTime,
	Tabs,
	TabsList,
	TabContent,
	TabItem,
	Table,
	Button,
	Inline,
} from "@sembark-travel/ui-base"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import React, { useEffect } from "react"
import { INotification, XHR } from "./store"
import {
	ListView,
	Search,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
	useSearch,
} from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { useHasFeatureFlag } from "../Auth"
import { generatePath } from "../router-utils"

export function Notification({
	notification,
}: {
	notification: INotification
}) {
	// content of the notification
	let NotificationRenderer: React.ComponentType<TNotificationRendererProps>
	let url = ""
	switch (notification.type) {
		case "App\\Notifications\\TripConverted":
			NotificationRenderer = TripConvertedNotificationRenderer
			break
		case "App\\Notifications\\FailedUpdateAmountForPaymentableNotification":
			NotificationRenderer = DefaultNotificationRenderer
			break
		case "App\\Notifications\\PaymentGatewayTransactionSuccessfulNotification":
		case "App\\Notifications\\PaymentGatewayTransactionFailedNotification":
			url = generatePath("/trips/:tripId", {
				tripId: String(notification.data.id),
			})
			NotificationRenderer = DefaultNotificationRenderer
			break
		case "App\\Notifications\\NewTripPlanRequestNotification":
			url = `/trip-plan-requests?source=${encodeURIComponent(
				String(notification.data.source || "all")
			)}`
			NotificationRenderer = DefaultNotificationRenderer
			break
		case "App\\Notifications\\TripPlanRequestsUpdatedNotification":
		case "App\\Notifications\\TripPlanRequestsAssignedNotification": {
			const idQuery = notification.data.id
				? `#${String(notification.data.id)}`
				: ""
			url = `/trip-plan-requests?q=${encodeURIComponent(
				idQuery
			)}&source=${encodeURIComponent(String(notification.data.source || "all"))}`
			NotificationRenderer = DefaultNotificationRenderer
			break
		}
		case "App\\IntegratedApps\\Facebook\\Notifications\\AccessTokenExpiredNotification":
			url = generatePath("/integrations/:integrationId", {
				integrationId: String(notification.data.integration),
			})
			NotificationRenderer = DefaultNotificationRenderer
			break
		case "App\\IntegratedApps\\WhatsApp\\Notifications\\WhatsAppMessageSentNotification":
			NotificationRenderer = WhatsappMessageNotificationRenderer
			break
		case "App\\IntegratedApps\\WhatsApp\\Notifications\\WhatsAppInboundMessageNotification":
		case "App\\IntegratedApps\\WhatsApp\\Notifications\\WhatsAppMessageReactionNotification":
			// Deep-link straight to the conversation in the WhatsApp inbox.
			if (notification.data.thread_id) {
				url = generatePath("/whatsapp/inbox/:threadId", {
					threadId: String(notification.data.thread_id),
				})
			}
			NotificationRenderer = WhatsappInboundMessageNotificationRenderer
			break
		case "App\\Notifications\\WorkflowPausedNotification":
			NotificationRenderer = DefaultNotificationRenderer
			break
		case "App\\Notifications\\BulkAssignSalesToTripsNotification":
			NotificationRenderer = DefaultNotificationRenderer
			url = "/trips"
			break
		case "App\\Notifications\\ExportCompletedNotification":
			NotificationRenderer = DefaultNotificationRenderer
			break
		case "App\\IntegratedApps\\Mailbox\\Notifications\\MailboxReconnectRequiredNotification":
			// Deep-link to the email integration so the owner can hit
			// Reconnect / fix credentials right away.
			if (notification.data.integration) {
				url = generatePath("/integrations/:integrationId", {
					integrationId: String(notification.data.integration),
				})
			}
			NotificationRenderer = DefaultNotificationRenderer
			break
		case "App\\IntegratedApps\\Mailbox\\Notifications\\MailboxSendFailedNotification": {
			// Link to the trip's emails (thread) tab, pre-filtered to the failed
			// message — the inbox-only /mail-messages search doesn't list sends.
			if (notification.data.trip_id) {
				const q = notification.data.q
					? `?q=${encodeURIComponent(String(notification.data.q))}`
					: ""
				url =
					generatePath("/trips/:tripId/emails", {
						tripId: String(notification.data.trip_id),
					}) + q
			}
			NotificationRenderer = DefaultNotificationRenderer
			break
		}
		case "App\\Notifications\\CommentWAReminderFailedNotification": {
			// Link to the task on whatever it hangs off, so its author can pick
			// up with the customer from there.
			const id = String(notification.data.commentable_id || "")
			switch (notification.data.commentable_type) {
				case "trips":
					url = generatePath("/trips/:tripId/comments", { tripId: id })
					break
				case "instalments":
					url = generatePath("/payments/instalments/:instalmentId", {
						instalmentId: id,
					})
					break
				case "trip_plan_requests":
					url = `/trip-plan-requests?q=${encodeURIComponent(
						`#${id}`
					)}&source=all`
					break
			}
			NotificationRenderer = DefaultNotificationRenderer
			break
		}
		case "App\\Notifications\\TripAssignNotification":
			NotificationRenderer = TripAssignNotificationRenderer
			break
		default:
			console.warn(`No renderer for '${notification.type}' type notification`)
			return null
	}
	return <NotificationRenderer notification={notification} url={url} />
}

type TNotificationRendererProps = {
	notification: INotification
	url: string
}

function TripConvertedNotificationRenderer({
	notification,
}: TNotificationRendererProps) {
	const { data } = notification
	const { id, price, converted_by } = data
	return (
		<Link to={generatePath("/trips/:tripId", { tripId: `${id}` })} anchored>
			<Stack gap="1">
				<Text fontWeight="semibold">Trip Conversion</Text>
				<Text fontSize="sm">
					<Badge>{String(price)}</Badge> by {String(converted_by)}
				</Text>
			</Stack>
		</Link>
	)
}

function DefaultNotificationRenderer({
	notification,
	url,
}: TNotificationRendererProps) {
	const { data } = notification
	const { title, description } = data
	const content = (
		<Stack gap="1">
			<Text fontWeight="semibold">{title}</Text>
			{description ? (
				<Text fontSize="sm" color="muted" whiteSpace="preserveLine">
					{description}
				</Text>
			) : null}
		</Stack>
	)
	return url ? (
		<Link to={url} anchored>
			{content}
		</Link>
	) : (
		content
	)
}

function WhatsappMessageNotificationRenderer({
	notification,
	url,
}: TNotificationRendererProps) {
	const { data } = notification
	const { title, description, message } = data
	return (
		<Link to={url} anchored>
			<Stack gap="2">
				<Text fontWeight="semibold">{title}</Text>
				{description ? (
					<Text fontSize="sm" color="muted" whiteSpace="preserveLine">
						{description}
					</Text>
				) : null}
				{message ? (
					<Stack gap="2">
						<Text fontSize={"sm"}>Message</Text>
						<Text fontSize="sm" color="muted" whiteSpace="preserveLine">
							{String(message)}
						</Text>
					</Stack>
				) : null}
			</Stack>
		</Link>
	)
}

function WhatsappInboundMessageNotificationRenderer({
	notification,
	url,
}: TNotificationRendererProps) {
	const { data } = notification
	const { title, description, preview, contact_name, contact_number } = data
	const from =
		[contact_name, contact_number].filter(Boolean).join(" · ") || undefined
	const content = (
		<Stack gap="1">
			<Text fontWeight="semibold">
				{String(title || "New WhatsApp message")}
			</Text>
			{from ? (
				<Text fontSize="sm" color="muted">
					{String(from)}
				</Text>
			) : description ? (
				<Text fontSize="sm" color="muted" whiteSpace="preserveLine">
					{description}
				</Text>
			) : null}
			{preview ? (
				<Text fontSize="sm" color="muted" whiteSpace="preserveLine">
					{String(preview)}
				</Text>
			) : null}
		</Stack>
	)
	return url ? (
		<Link to={url} anchored>
			{content}
		</Link>
	) : (
		content
	)
}

function TripAssignNotificationRenderer({
	notification,
}: TNotificationRendererProps) {
	const { id, assigned_by } = notification.data

	return (
		<Link to={generatePath("/trips/:tripId", { tripId: `${id}` })} anchored>
			<Stack gap="1">
				<Text fontWeight="semibold">Trip Assigned</Text>

				<Inline gap="1" alignItems="center">
					<Inline
						as="span"
						gap="1"
						alignItems="center"
						color="accent"
						fontWeight="semibold"
						fontSize="sm"
					>
						<Text fontSize="sm">Trip #{`${id}`}</Text>
						<Icons.ChevronDown rotate="270" size="3" opacity="50" />
					</Inline>

					<Text fontSize="sm">by {String(assigned_by)}</Text>
				</Inline>
			</Stack>
		</Link>
	)
}

type TFilters = TSearchParams & {
	status: "all" | "unread"
}

export function NotificationList() {
	const [query, setQuery] = useLocationQuery<TFilters, TFilters>()
	const [params, setParams] = useSearch<TFilters>({
		...query,
		status: query.status || "unread",
	})
	useEffect(() => setQuery(params), [params, setQuery])
	const xhr = useXHR()
	// Push notifications are only available when the server has Pusher Beams
	// credentials configured.
	const hasPushNotifications = useHasFeatureFlag("push_notifications")
	// mark all as seen
	useEffect(() => {
		XHR(xhr).markAllAsSeen()
	}, [xhr])
	return (
		<Search
			title="Notifications"
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			areAdvancedFiltersApplied={({ status, ...params }) =>
				areAdvancedFiltersAppliedDefault(params)
			}
			actions={
				hasPushNotifications ? (
					<ButtonLink
						to={generatePath("/notifications/preferences")}
						title="Notification settings"
					>
						<Icons.Cog />
					</ButtonLink>
				) : null
			}
		>
			{({ searchParams, setSearchParamValue }) => (
				<Tabs>
					<TabsList>
						<TabItem
							active={searchParams.status === "unread"}
							onClick={() => setSearchParamValue("status", "unread")}
						>
							Unread
						</TabItem>
						<TabItem
							active={searchParams.status === "all"}
							onClick={() => setSearchParamValue("status", "all")}
						>
							All
						</TabItem>
					</TabsList>
					<TabContent active>
						<ListView<INotification, TFilters>
							pageKey="notifications"
							params={params}
							onPageChange={(page) => setParams({ ...params, page })}
							swrConfig={{ revalidateOnFocus: true }}
							fetch={(xhr, { status, ...params }) =>
								XHR(xhr).getNotifications({
									...params,
									unread: status === "unread" ? 1 : null,
								})
							}
						>
							{({ items: notifications, xhr, refresh }) => (
								<Table
									headers={["Details", "Created", ""]}
									bordered
									hover
									alignCols={{ 2: "right" }}
									rows={notifications.map((n) => [
										<Notification notification={n} />,
										<RelativeTime timestamp={n.created_at} />,
										!n.read_at ? (
											<Button
												size="sm"
												onClick={() =>
													XHR(xhr)
														.markAsRead([n])
														.then(() => {
															refresh()
														})
												}
											>
												<Icons.Ok />
											</Button>
										) : null,
									])}
								/>
							)}
						</ListView>
					</TabContent>
				</Tabs>
			)}
		</Search>
	)
}
